import type { Gender } from '@pflegenavi/shared/api';

/**
 * Extended receipt details with fee information for redistribution calculations
 */
export interface BatchReceiptDetails {
  id: string;
  residentId?: string;
  residentName?: string;
  residentGender?: Gender;
  amount?: number;
  additionalFeeAmount?: number;
  includedFeeAmount?: number;
  notes?: string;
  receiptImageIds?: string[] | null;
  residentActive?: boolean;
  individualDate?: Date;
}

/**
 * Splits an amount evenly across parts. Used when total receipt amounts are zero.
 *
 * @param amount The total amount to split
 * @param parts Number of parts to split into
 * @returns Array of amounts for each part
 */
function splitAmountEvenly(amount: number, parts: number): number[] {
  if (parts <= 0) {
    return [];
  }

  const base =
    amount >= 0 ? Math.floor(amount / parts) : Math.ceil(amount / parts);

  const remainder = amount - base * parts;
  const step = remainder >= 0 ? 1 : -1;

  const result = Array(parts).fill(base);

  // Add remainder to the last entry to ensure total equals exactly
  for (let i = 0; i < Math.abs(remainder); i++) {
    result[result.length - 1 - i] += step;
  }
  return result;
}

/**
 * Distribute a fixed fee across receipts proportional to their amounts.
 * Deterministic and stable (ties broken by original index).
 * Matches the backend implementation in nursing_home_receipt_batch.ex
 *
 * @param receiptAmountsCents non-negative integers (cents)
 * @param feeCents integer (can be negative for discounts)
 * @returns an array of integers (cents) summing exactly to feeCents
 */
export function distributeProportionally(
  receiptAmountsCents: number[],
  feeCents: number
): number[] {
  const n = receiptAmountsCents.length;

  if (n === 0) {
    return [];
  }

  if (feeCents === 0) {
    return new Array(n).fill(0);
  }

  const total = receiptAmountsCents.reduce((sum, cur) => sum + cur, 0);

  if (total === 0) {
    return splitAmountEvenly(feeCents, n);
  }

  const sign = feeCents < 0 ? -1 : 1;
  const absFee = Math.abs(feeCents);

  // Step 1: base shares via floor( absFee * amount / total )
  const baseShares = new Array<number>(n);
  const remainders = new Array<number>(n);
  let baseSum = 0;

  for (let i = 0; i < n; i++) {
    const numer = absFee * receiptAmountsCents[i];
    const base = Math.floor(numer / total);
    const remainder = numer - base * total;

    baseShares[i] = base;
    remainders[i] = remainder;
    baseSum += base;
  }

  // Step 2: distribute the remaining cents to the largest remainders
  const remaining = absFee - baseSum;

  // Sort indices by remainder desc, then by original index desc (stable)
  const idx = Array.from({ length: n }, (_, i) => i);
  idx.sort((a, b) => {
    const difference = remainders[b] - remainders[a];
    return difference !== 0 ? difference : b - a;
  });

  // Split remainder among the entries
  for (let k = 0; k < remaining; k++) {
    baseShares[idx[k]] += 1;
  }

  // Apply sign back
  return baseShares.map((v) => sign * v);
}

/**
 * Redistributes fees across receipt batch entries.
 * Matches the backend proportional distribution logic.
 *
 * - If no fee amount, all entries get 0 fees
 * - If total receipt amounts are 0, distributes fees equally
 * - Otherwise, distributes fees proportionally based on receipt amounts
 * - Handles rounding errors by adjusting the entry with the largest amount
 *
 * @param receipts Array of receipt batch entries
 * @param separateFeeAmount Total separate fee amount (additional_fee_amount)
 * @param immediateFeeAmount Total immediate fee amount (included_fee_amount)
 * @returns Updated receipts with redistributed fees
 */
export const redistributeFees = (
  receipts: BatchReceiptDetails[],
  separateFeeAmount: number,
  immediateFeeAmount: number
): BatchReceiptDetails[] => {
  const numberOfEntries = receipts.length;

  if (numberOfEntries === 0) {
    return receipts;
  }

  // No fees to distribute
  if (separateFeeAmount === 0 && immediateFeeAmount === 0) {
    return receipts.map((receipt) => ({
      ...receipt,
      additionalFeeAmount: 0,
      includedFeeAmount: 0,
    }));
  }

  const receiptAmounts = receipts.map((receipt) => receipt.amount ?? 0);

  const separateParts = distributeProportionally(
    receiptAmounts,
    Math.abs(separateFeeAmount)
  );
  const immediateParts = distributeProportionally(
    receiptAmounts,
    Math.abs(immediateFeeAmount)
  );

  return receipts.map((receipt, index) => ({
    ...receipt,
    additionalFeeAmount: separateParts[index],
    includedFeeAmount: immediateParts[index],
  }));
};
